"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { appendSkippedDropId, getDisplayQuickVoteAmounts, getMemesQuickVoteRemainingCount } from "@/hooks/memesQuickVote.helpers";
import { useMemesQuickVoteActiveDrop } from "@/hooks/useMemesQuickVoteActiveDrop";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { useMemesQuickVoteDiscovery } from "@/hooks/useMemesQuickVoteDiscovery";
import { useMemesQuickVoteHiddenSummaryCleanup } from "@/hooks/useMemesQuickVoteHiddenSummaryCleanup";
import { createInitialOptimisticRemainingPowerState, getOptimisticRemainingPowerKey, type OptimisticRemainingPowerState, useMemesQuickVoteOptimisticState, useMemesQuickVoteVoteHandlers } from "@/hooks/useMemesQuickVoteQueue.optimistic";
import { useMemesQuickVoteStorage } from "@/hooks/useMemesQuickVoteStorage";
import { useMemesQuickVoteSubmit } from "@/hooks/useMemesQuickVoteSubmit";
import { useMemesQuickVoteSummary } from "@/hooks/useMemesQuickVoteSummary";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useCallback, useContext, useMemo, useState } from "react";
type UseMemesQuickVoteQueueOptions = {
  readonly enabled?: boolean | undefined;
  readonly sessionId: number;
};
type UseMemesQuickVoteQueueResult = {
  readonly activeDrop: ExtendedDrop | null;
  readonly hasDiscoveryError: boolean;
  readonly isExhausted: boolean;
  readonly isLoading: boolean;
  readonly isReady: boolean;
  readonly isVoting: boolean;
  readonly latestUsedAmount: number | null;
  readonly queue: ExtendedDrop[];
  readonly recentAmounts: number[];
  readonly remainingCount: number;
  readonly retryDiscovery: () => void;
  readonly submitVote: (drop: ExtendedDrop, amount: number | string) => Promise<boolean>;
  readonly skipDrop: (drop: ExtendedDrop) => void;
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
};
type UseMemesQuickVoteQueueDerivedState = Pick<UseMemesQuickVoteQueueResult, "activeDrop" | "hasDiscoveryError" | "isExhausted" | "isLoading" | "isReady" | "latestUsedAmount" | "queue" | "recentAmounts" | "remainingCount" | "uncastPower" | "votingLabel">;
type UseDerivedMemesQuickVoteQueueStateOptions = {
  readonly activeDropCandidate: ExtendedDrop | null;
  readonly activeIsLoading: boolean;
  readonly discoveredQueue: readonly ApiDrop[];
  readonly effectiveOptimisticRemainingPower: number | null;
  readonly enabled: boolean;
  readonly hasPageFetchError: boolean;
  readonly hiddenSkippedCount: number;
  readonly isDiscoveryExhausted: boolean;
  readonly isFetchingPage: boolean;
  readonly isSettingsLoaded: boolean;
  readonly isSummaryPending: boolean;
  readonly isSummaryQuickVoteEnabled: boolean;
  readonly isSummarySuccess: boolean;
  readonly recentAmountsByRecency: readonly number[];
  readonly serverCount: number | null;
  readonly summaryCount: number;
  readonly unreflectedVoteCount: number;
  readonly summaryVotingLabel: string | null;
};
type UseMemesQuickVoteQueueActionsOptions = {
  readonly activeCandidateId: string | null;
  readonly discoveredDropsById: Record<string, ApiDrop>;
  readonly enabled: boolean;
  readonly hideDropId: (dropId: string) => void;
  readonly invalidateDrops: React.ContextType<typeof ReactQueryWrapperContext>["invalidateDrops"];
  readonly isQuickVoteEnabled: boolean;
  readonly isSummarySuccess: boolean;
  readonly nextCandidateId: string | null;
  readonly optimisticRemainingPowerKey: string;
  readonly optimisticRemainingPowerState: OptimisticRemainingPowerState;
  readonly refetchSummary: () => Promise<unknown>;
  readonly removeDropId: (dropId: string) => void;
  readonly requestAuth: React.ContextType<typeof AuthContext>["requestAuth"];
  readonly resyncDiscovery: () => void;
  readonly setAndPersistRecentAmounts: (updater: (current: number[]) => number[]) => void;
  readonly setAndPersistSkippedDropIds: (updater: (current: string[]) => string[]) => void;
  readonly setOptimisticRemainingPowerState: React.Dispatch<React.SetStateAction<OptimisticRemainingPowerState>>;
  readonly setToast: React.ContextType<typeof AuthContext>["setToast"];
  readonly summaryCount: number;
};
const clampDropMaxRating = (drop: ExtendedDrop, optimisticRemainingPower: number | null): ExtendedDrop => {
  const profileContext = drop.context_profile_context;
  const maxRating = profileContext?.max_rating;
  if (optimisticRemainingPower === null || profileContext?.rating !== 0 || typeof maxRating !== "number") {
    return drop;
  }
  const nextMaxRating = Math.max(0, Math.min(maxRating, optimisticRemainingPower));
  if (nextMaxRating === maxRating) {
    return drop;
  }
  return {
    ...drop,
    context_profile_context: {
      ...profileContext,
      max_rating: nextMaxRating
    }
  };
};
const isVoteableQuickVoteDrop = (drop: ExtendedDrop | null): boolean => {
  const profileContext = drop?.context_profile_context;
  return profileContext?.rating === 0 && typeof profileContext.max_rating === "number" && profileContext.max_rating > 0;
};
const useDerivedMemesQuickVoteQueueState = t0 => {
  const $ = _c(36);
  const {
    activeDropCandidate,
    activeIsLoading,
    discoveredQueue,
    effectiveOptimisticRemainingPower,
    enabled,
    hasPageFetchError,
    hiddenSkippedCount,
    isDiscoveryExhausted,
    isFetchingPage,
    isSettingsLoaded,
    isSummaryPending,
    isSummaryQuickVoteEnabled,
    isSummarySuccess,
    recentAmountsByRecency,
    serverCount,
    summaryCount,
    unreflectedVoteCount,
    summaryVotingLabel
  } = t0;
  let t1;
  if ($[0] !== activeDropCandidate || $[1] !== effectiveOptimisticRemainingPower) {
    t1 = activeDropCandidate ? clampDropMaxRating(activeDropCandidate, effectiveOptimisticRemainingPower) : null;
    $[0] = activeDropCandidate;
    $[1] = effectiveOptimisticRemainingPower;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const clampedActiveDrop = t1;
  let t2;
  if ($[3] !== clampedActiveDrop) {
    t2 = isVoteableQuickVoteDrop(clampedActiveDrop) ? clampedActiveDrop : null;
    $[3] = clampedActiveDrop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeDrop = t2;
  let t3;
  if ($[5] !== clampedActiveDrop || $[6] !== discoveredQueue || $[7] !== effectiveOptimisticRemainingPower) {
    bb0: {
      let t4;
      if ($[9] !== effectiveOptimisticRemainingPower) {
        t4 = drop => clampDropMaxRating(drop, effectiveOptimisticRemainingPower);
        $[9] = effectiveOptimisticRemainingPower;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      const clampedQueue = discoveredQueue.map(convertApiDropToExtendedDrop).map(t4);
      if (!clampedActiveDrop) {
        t3 = clampedQueue;
        break bb0;
      }
      let t5;
      if ($[11] !== clampedActiveDrop) {
        t5 = drop_0 => drop_0.id === clampedActiveDrop.id ? clampedActiveDrop : drop_0;
        $[11] = clampedActiveDrop;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      t3 = clampedQueue.map(t5);
    }
    $[5] = clampedActiveDrop;
    $[6] = discoveredQueue;
    $[7] = effectiveOptimisticRemainingPower;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const queue = t3;
  let t4;
  if ($[13] !== recentAmountsByRecency) {
    t4 = getDisplayQuickVoteAmounts(recentAmountsByRecency);
    $[13] = recentAmountsByRecency;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const recentAmounts = t4;
  let t5;
  if ($[15] !== recentAmountsByRecency) {
    t5 = recentAmountsByRecency.at(-1) ?? null;
    $[15] = recentAmountsByRecency;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const latestUsedAmount = t5;
  const uncastPower = activeDrop?.context_profile_context?.max_rating ?? null;
  const votingLabel = activeDrop ? WAVE_VOTING_LABELS[activeDrop.wave.voting_credit_type] : summaryVotingLabel;
  let t6;
  if ($[17] !== hiddenSkippedCount || $[18] !== isSummarySuccess || $[19] !== queue.length || $[20] !== serverCount || $[21] !== summaryCount || $[22] !== unreflectedVoteCount) {
    t6 = isSummarySuccess ? getMemesQuickVoteRemainingCount({
      count: summaryCount,
      floor: queue.length,
      hiddenCount: hiddenSkippedCount,
      optimisticVoteCount: unreflectedVoteCount
    }) : getMemesQuickVoteRemainingCount({
      count: serverCount,
      floor: queue.length,
      hiddenCount: hiddenSkippedCount
    });
    $[17] = hiddenSkippedCount;
    $[18] = isSummarySuccess;
    $[19] = queue.length;
    $[20] = serverCount;
    $[21] = summaryCount;
    $[22] = unreflectedVoteCount;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const remainingCount = t6;
  const hasDiscoveryError = enabled && hasPageFetchError;
  const isQuickVoteUnavailable = enabled && isSettingsLoaded && !isSummaryQuickVoteEnabled;
  const activeProfileContext = clampedActiveDrop?.context_profile_context;
  const isPowerExhausted = enabled && !hasDiscoveryError && activeProfileContext?.rating === 0 && typeof activeProfileContext.max_rating === "number" && activeProfileContext.max_rating <= 0;
  const isExhausted = enabled && (isQuickVoteUnavailable || isDiscoveryExhausted || isPowerExhausted);
  const isLoading = enabled && !hasDiscoveryError && !isExhausted && !activeDropCandidate && (!isSettingsLoaded || isSummaryQuickVoteEnabled && isSummaryPending || isFetchingPage || activeIsLoading);
  const t7 = activeDrop !== null;
  let t8;
  if ($[24] !== activeDrop || $[25] !== hasDiscoveryError || $[26] !== isExhausted || $[27] !== isLoading || $[28] !== latestUsedAmount || $[29] !== queue || $[30] !== recentAmounts || $[31] !== remainingCount || $[32] !== t7 || $[33] !== uncastPower || $[34] !== votingLabel) {
    t8 = {
      activeDrop,
      hasDiscoveryError,
      isExhausted,
      isLoading,
      isReady: t7,
      latestUsedAmount,
      queue,
      recentAmounts,
      remainingCount,
      uncastPower,
      votingLabel
    };
    $[24] = activeDrop;
    $[25] = hasDiscoveryError;
    $[26] = isExhausted;
    $[27] = isLoading;
    $[28] = latestUsedAmount;
    $[29] = queue;
    $[30] = recentAmounts;
    $[31] = remainingCount;
    $[32] = t7;
    $[33] = uncastPower;
    $[34] = votingLabel;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
};
const useMemesQuickVoteQueueActions = t0 => {
  const $ = _c(39);
  const {
    activeCandidateId,
    discoveredDropsById,
    enabled,
    hideDropId,
    invalidateDrops,
    isQuickVoteEnabled,
    isSummarySuccess,
    nextCandidateId,
    optimisticRemainingPowerKey,
    optimisticRemainingPowerState,
    refetchSummary,
    removeDropId,
    requestAuth,
    resyncDiscovery,
    setAndPersistRecentAmounts,
    setAndPersistSkippedDropIds,
    setOptimisticRemainingPowerState,
    setToast,
    summaryCount
  } = t0;
  let t1;
  if ($[0] !== isSummarySuccess || $[1] !== optimisticRemainingPowerKey || $[2] !== optimisticRemainingPowerState || $[3] !== summaryCount) {
    t1 = {
      isSummarySuccess,
      optimisticRemainingPowerKey,
      optimisticRemainingPowerState,
      summaryCount
    };
    $[0] = isSummarySuccess;
    $[1] = optimisticRemainingPowerKey;
    $[2] = optimisticRemainingPowerState;
    $[3] = summaryCount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    hasPendingOptimisticVotes,
    optimisticRemainingPower,
    summaryOptimismState
  } = useMemesQuickVoteOptimisticState(t1);
  let t2;
  if ($[5] !== isSummarySuccess || $[6] !== optimisticRemainingPowerKey || $[7] !== refetchSummary || $[8] !== removeDropId || $[9] !== resyncDiscovery || $[10] !== setAndPersistSkippedDropIds || $[11] !== setOptimisticRemainingPowerState || $[12] !== summaryCount) {
    t2 = {
      isSummarySuccess,
      optimisticRemainingPowerKey,
      refetchSummary,
      removeDropId,
      resyncDiscovery,
      setAndPersistSkippedDropIds,
      setOptimisticRemainingPowerState,
      summaryCount
    };
    $[5] = isSummarySuccess;
    $[6] = optimisticRemainingPowerKey;
    $[7] = refetchSummary;
    $[8] = removeDropId;
    $[9] = resyncDiscovery;
    $[10] = setAndPersistSkippedDropIds;
    $[11] = setOptimisticRemainingPowerState;
    $[12] = summaryCount;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const {
    handleInvalidatedDrop,
    handleVoteFailure,
    handleVoteQueued,
    handleVoteSuccess
  } = useMemesQuickVoteVoteHandlers(t2);
  const t3 = enabled && isQuickVoteEnabled;
  let t4;
  if ($[14] !== activeCandidateId || $[15] !== discoveredDropsById || $[16] !== handleInvalidatedDrop || $[17] !== nextCandidateId || $[18] !== t3) {
    t4 = {
      activeCandidateId,
      discoveredDropsById,
      enabled: t3,
      nextCandidateId,
      onInvalidatedDrop: handleInvalidatedDrop
    };
    $[14] = activeCandidateId;
    $[15] = discoveredDropsById;
    $[16] = handleInvalidatedDrop;
    $[17] = nextCandidateId;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const active = useMemesQuickVoteActiveDrop(t4);
  const effectiveOptimisticRemainingPower = active.hasFreshData && !hasPendingOptimisticVotes ? null : optimisticRemainingPower;
  let t5;
  if ($[20] !== handleVoteFailure || $[21] !== handleVoteQueued || $[22] !== handleVoteSuccess || $[23] !== invalidateDrops || $[24] !== requestAuth || $[25] !== setAndPersistRecentAmounts || $[26] !== setAndPersistSkippedDropIds || $[27] !== setToast) {
    t5 = {
      requestAuth,
      setToast,
      invalidateDrops,
      onVoteFailure: handleVoteFailure,
      onVoteQueued: handleVoteQueued,
      onVoteSuccess: handleVoteSuccess,
      setAndPersistRecentAmounts,
      setAndPersistSkippedDropIds
    };
    $[20] = handleVoteFailure;
    $[21] = handleVoteQueued;
    $[22] = handleVoteSuccess;
    $[23] = invalidateDrops;
    $[24] = requestAuth;
    $[25] = setAndPersistRecentAmounts;
    $[26] = setAndPersistSkippedDropIds;
    $[27] = setToast;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  const {
    isVoting,
    submitVote
  } = useMemesQuickVoteSubmit(t5);
  let t6;
  if ($[29] !== hideDropId || $[30] !== setAndPersistSkippedDropIds) {
    t6 = drop => {
      hideDropId(drop.id);
      setAndPersistSkippedDropIds(current => appendSkippedDropId(current, drop.id));
    };
    $[29] = hideDropId;
    $[30] = setAndPersistSkippedDropIds;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const skipDrop = t6;
  let t7;
  if ($[32] !== active || $[33] !== effectiveOptimisticRemainingPower || $[34] !== isVoting || $[35] !== skipDrop || $[36] !== submitVote || $[37] !== summaryOptimismState) {
    t7 = {
      active,
      effectiveOptimisticRemainingPower,
      isVoting,
      skipDrop,
      submitVote,
      summaryOptimismState
    };
    $[32] = active;
    $[33] = effectiveOptimisticRemainingPower;
    $[34] = isVoting;
    $[35] = skipDrop;
    $[36] = submitVote;
    $[37] = summaryOptimismState;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
};
export const useMemesQuickVoteQueue = t0 => {
  const $ = _c(73);
  const {
    enabled: t1,
    sessionId
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const {
    invalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const {
    contextProfile,
    isLoaded: isSettingsLoaded,
    isEnabled: isQuickVoteEnabled,
    memesWaveId,
    proxyId
  } = useMemesQuickVoteContext();
  let t2;
  if ($[0] !== contextProfile || $[1] !== memesWaveId || $[2] !== sessionId) {
    t2 = getOptimisticRemainingPowerKey({
      contextProfile,
      memesWaveId,
      sessionId
    });
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = sessionId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const optimisticRemainingPowerKey = t2;
  let t3;
  if ($[4] !== optimisticRemainingPowerKey) {
    t3 = () => createInitialOptimisticRemainingPowerState(optimisticRemainingPowerKey);
    $[4] = optimisticRemainingPowerKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [optimisticRemainingPowerState, setOptimisticRemainingPowerState] = useState(t3);
  let t4;
  if ($[6] !== contextProfile || $[7] !== memesWaveId || $[8] !== proxyId) {
    t4 = {
      contextProfile,
      memesWaveId,
      proxyId
    };
    $[6] = contextProfile;
    $[7] = memesWaveId;
    $[8] = proxyId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    recentAmountsByRecency,
    setAndPersistRecentAmounts,
    setAndPersistSkippedDropIds,
    skippedDropIds
  } = useMemesQuickVoteStorage(t4);
  let t5;
  if ($[10] !== enabled) {
    t5 = {
      enabled
    };
    $[10] = enabled;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const summary = useMemesQuickVoteSummary(t5);
  let t6;
  if ($[12] !== enabled || $[13] !== sessionId || $[14] !== skippedDropIds) {
    t6 = {
      enabled,
      sessionId,
      skippedDropIds
    };
    $[12] = enabled;
    $[13] = sessionId;
    $[14] = skippedDropIds;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const discovery = useMemesQuickVoteDiscovery(t6);
  const {
    activeCandidateId,
    discoveredDropsById,
    hasPageFetchError,
    hideDropId,
    isExhausted: isDiscoveryExhausted,
    isFetchingPage,
    nextCandidateId,
    queue: discoveredQueue,
    removeDropId,
    resyncDiscovery,
    retryDiscovery,
    serverCount
  } = discovery;
  const {
    count: summaryCount,
    firstDrop: summaryFirstDrop,
    isPending: isSummaryPending,
    isQuickVoteEnabled: isSummaryQuickVoteEnabled,
    isSuccess: isSummarySuccess,
    refetch: refetchSummary,
    stats: summaryStats
  } = summary;
  let t7;
  if ($[16] !== activeCandidateId || $[17] !== discoveredDropsById || $[18] !== enabled || $[19] !== hideDropId || $[20] !== invalidateDrops || $[21] !== isQuickVoteEnabled || $[22] !== isSummarySuccess || $[23] !== nextCandidateId || $[24] !== optimisticRemainingPowerKey || $[25] !== optimisticRemainingPowerState || $[26] !== refetchSummary || $[27] !== removeDropId || $[28] !== requestAuth || $[29] !== resyncDiscovery || $[30] !== setAndPersistRecentAmounts || $[31] !== setAndPersistSkippedDropIds || $[32] !== setToast || $[33] !== summaryCount) {
    t7 = {
      activeCandidateId,
      discoveredDropsById,
      enabled,
      hideDropId,
      invalidateDrops,
      isQuickVoteEnabled,
      isSummarySuccess,
      nextCandidateId,
      optimisticRemainingPowerKey,
      optimisticRemainingPowerState,
      refetchSummary,
      removeDropId,
      requestAuth,
      resyncDiscovery,
      setAndPersistRecentAmounts,
      setAndPersistSkippedDropIds,
      setOptimisticRemainingPowerState,
      setToast,
      summaryCount
    };
    $[16] = activeCandidateId;
    $[17] = discoveredDropsById;
    $[18] = enabled;
    $[19] = hideDropId;
    $[20] = invalidateDrops;
    $[21] = isQuickVoteEnabled;
    $[22] = isSummarySuccess;
    $[23] = nextCandidateId;
    $[24] = optimisticRemainingPowerKey;
    $[25] = optimisticRemainingPowerState;
    $[26] = refetchSummary;
    $[27] = removeDropId;
    $[28] = requestAuth;
    $[29] = resyncDiscovery;
    $[30] = setAndPersistRecentAmounts;
    $[31] = setAndPersistSkippedDropIds;
    $[32] = setToast;
    $[33] = summaryCount;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const {
    active,
    effectiveOptimisticRemainingPower,
    isVoting,
    skipDrop,
    submitVote,
    summaryOptimismState
  } = useMemesQuickVoteQueueActions(t7);
  let t8;
  if ($[35] !== refetchSummary || $[36] !== removeDropId || $[37] !== setAndPersistSkippedDropIds) {
    t8 = dropId => {
      removeDropId(dropId);
      setAndPersistSkippedDropIds(current => current.filter(value => value !== dropId));
      refetchSummary();
    };
    $[35] = refetchSummary;
    $[36] = removeDropId;
    $[37] = setAndPersistSkippedDropIds;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const clearHiddenSummaryDrop = t8;
  let t9;
  if ($[39] !== activeCandidateId || $[40] !== clearHiddenSummaryDrop || $[41] !== enabled || $[42] !== isFetchingPage || $[43] !== isSummarySuccess || $[44] !== memesWaveId || $[45] !== skippedDropIds || $[46] !== summaryFirstDrop) {
    t9 = {
      activeCandidateId,
      enabled,
      isFetchingPage,
      isSummarySuccess,
      memesWaveId,
      onClearHiddenSummaryDrop: clearHiddenSummaryDrop,
      skippedDropIds,
      summaryFirstDrop
    };
    $[39] = activeCandidateId;
    $[40] = clearHiddenSummaryDrop;
    $[41] = enabled;
    $[42] = isFetchingPage;
    $[43] = isSummarySuccess;
    $[44] = memesWaveId;
    $[45] = skippedDropIds;
    $[46] = summaryFirstDrop;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  useMemesQuickVoteHiddenSummaryCleanup(t9);
  let t10;
  if ($[48] !== active.activeDrop || $[49] !== active.isLoading || $[50] !== discoveredQueue || $[51] !== effectiveOptimisticRemainingPower || $[52] !== enabled || $[53] !== hasPageFetchError || $[54] !== isDiscoveryExhausted || $[55] !== isFetchingPage || $[56] !== isSettingsLoaded || $[57] !== isSummaryPending || $[58] !== isSummaryQuickVoteEnabled || $[59] !== isSummarySuccess || $[60] !== recentAmountsByRecency || $[61] !== serverCount || $[62] !== skippedDropIds.length || $[63] !== summaryCount || $[64] !== summaryOptimismState.unreflectedVoteCount || $[65] !== summaryStats.votingLabel) {
    t10 = {
      activeDropCandidate: active.activeDrop,
      activeIsLoading: active.isLoading,
      discoveredQueue,
      effectiveOptimisticRemainingPower,
      enabled,
      hasPageFetchError,
      hiddenSkippedCount: skippedDropIds.length,
      isDiscoveryExhausted,
      isFetchingPage,
      isSettingsLoaded,
      isSummaryPending,
      isSummaryQuickVoteEnabled,
      isSummarySuccess,
      recentAmountsByRecency,
      serverCount,
      summaryCount,
      unreflectedVoteCount: summaryOptimismState.unreflectedVoteCount,
      summaryVotingLabel: summaryStats.votingLabel
    };
    $[48] = active.activeDrop;
    $[49] = active.isLoading;
    $[50] = discoveredQueue;
    $[51] = effectiveOptimisticRemainingPower;
    $[52] = enabled;
    $[53] = hasPageFetchError;
    $[54] = isDiscoveryExhausted;
    $[55] = isFetchingPage;
    $[56] = isSettingsLoaded;
    $[57] = isSummaryPending;
    $[58] = isSummaryQuickVoteEnabled;
    $[59] = isSummarySuccess;
    $[60] = recentAmountsByRecency;
    $[61] = serverCount;
    $[62] = skippedDropIds.length;
    $[63] = summaryCount;
    $[64] = summaryOptimismState.unreflectedVoteCount;
    $[65] = summaryStats.votingLabel;
    $[66] = t10;
  } else {
    t10 = $[66];
  }
  const derivedState = useDerivedMemesQuickVoteQueueState(t10);
  let t11;
  if ($[67] !== derivedState || $[68] !== isVoting || $[69] !== retryDiscovery || $[70] !== skipDrop || $[71] !== submitVote) {
    t11 = {
      ...derivedState,
      isVoting,
      retryDiscovery,
      submitVote,
      skipDrop
    };
    $[67] = derivedState;
    $[68] = isVoting;
    $[69] = retryDiscovery;
    $[70] = skipDrop;
    $[71] = submitVote;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  return t11;
};